import { APP_BASE_HREF, Location } from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { EnvironmentProviders, makeEnvironmentProviders } from "@angular/core";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";

import { environment } from "src/environments";
import { appendUrlPath } from "src/utils";

const {
  appIds,
  clientId,
  loginAuthorityPolicyName,
  passwordResetPolicyName,
  tenantName,
} = environment.azureAD;

const authorityDomain = `${tenantName}.b2clogin.com`;
const rootUrl = `https://${authorityDomain}/tfp/${tenantName}.onmicrosoft.com`;

const authority = `${rootUrl}/${loginAuthorityPolicyName}`;

export const passwordResetAuthority = `${rootUrl}/${passwordResetPolicyName}`;

function msalInstanceFactory(baseHref: string): IPublicClientApplication {
  const baseUrl = appendUrlPath(window.location.origin, baseHref);

  return new PublicClientApplication({
    auth: {
      authority,
      clientId,
      knownAuthorities: [authorityDomain],
      redirectUri: appendUrlPath(baseUrl, "login").href,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
  });
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      prompt: "select_account",
    },
  };
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();

  const { mr, mrv2 } = environment.apiUrls;

  protectedResourceMap.set(
    mr.href,
    getMRScopes(["read", "write", "user_impersonation"]),
  );

  // MSAL strips trailing slashes before matching, so `/api/` covers `/apiv2/`.
  // Only register MRv2 when the MR entry does not already cover it, avoiding
  // duplicate-match warnings.
  if (!mrv2.href.startsWith(Location.stripTrailingSlash(mr.href))) {
    protectedResourceMap.set(
      mrv2.href,
      getMRScopes(["read", "write", "user_impersonation"]),
    );
  }

  protectedResourceMap.set(
    `${environment.ymsUrls.app.href}api`,
    getMRScopes(["write", "user_impersonation"]),
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function provideAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
      deps: [APP_BASE_HREF],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ]);
}

function getMRScopes(scopeNames: string[]): string[] {
  return scopeNames.map(
    (name) => `https://${tenantName}.onmicrosoft.com/${appIds.mr}/${name}`,
  );
}
